import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import moment from 'moment';
import { Label, Modal } from '../../../Components/uielements';
import { ModalWrapper } from '../../../services/settings/appStyle/global.style';
import { Signature } from './logRecords.style';
import { getCheckListDocument } from '../../../services/bioplus-services/checkList';

const resolveDocumentSrc = (res) => {
  if (!res) return null;
  if (res instanceof Blob) return URL.createObjectURL(res);
  if (typeof res === 'string') return res;
  if (res.documentUrl instanceof Blob) return URL.createObjectURL(res.documentUrl);
  if (typeof res.documentUrl === 'string') return res.documentUrl;
  if (res.data instanceof Blob) return URL.createObjectURL(res.data);
  if (typeof res.data === 'string') return res.data;
  return null;
};

const srcToBase64 = async (src) => {
  const response = await fetch(src);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const DeclarationModal = ({ open, onClose, userData }) => {
  const [mediaMap, setMediaMap] = useState({});
  const fetchingRef = useRef(new Set());

  useEffect(() => {
    if (open) setMediaMap({});
    fetchingRef.current = new Set();
  }, [open, userData?._id]);

  const fetchMedia = async (documentId, index) => {
    if (fetchingRef.current.has(index)) return;
    setMediaMap((prev) => {
      if (prev[index]) return prev;
      return { ...prev, [index]: { src: null, base64: null, loading: true, error: false } };
    });

    try {
      const res = await getCheckListDocument({
        documentId,
        responseId: userData?._id,
        listType: 'DECLARATION',
      });

      const src = resolveDocumentSrc(res);

      let base64 = null;
      if (src) {
        try {
          base64 = await srcToBase64(src);
        } catch {
          // base64 conversion failed — modal can still show blob URL, PDF will show placeholder
        }
      }

      setMediaMap((prev) => ({
        ...prev,
        [index]: { src, base64, loading: false, error: !src },
      }));
    } catch {
      setMediaMap((prev) => ({
        ...prev,
        [index]: { src: null, base64: null, loading: false, error: true },
      }));
    }
  };
  useEffect(() => {
    if (!open || !userData?.checklistResponse) return;
    userData.checklistResponse.forEach((item, index) => {
      if (item.type === 'MEDIA' && item.response) {
        fetchMedia(item.response, index);
      }
    });
  }, [open, userData?._id]);

  const generatePDF = async () => {
    const doc = new jsPDF();
    let y = 20;
    const maxWidth = 170;
    const pageHeight = doc.internal.pageSize.height;
    const fileName = moment(userData?.createdAt).format('ddd-DD-MM-YYYY-hh.mm A');

    const addWrappedText = (text, x, currY, isBold = false) => {
      doc.setFont(undefined, isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach((line) => {
        if (currY + 10 > pageHeight) {
          doc.addPage();
          currY = 20;
        }
        doc.text(line, x, currY);
        currY += 7;
      });
      doc.setFont(undefined, 'normal');
      return currY;
    };

    const addLabelValue = (label, value, x, currY) => {
      doc.setFont(undefined, 'bold');
      const labelWidth = doc.getTextWidth(label);
      doc.text(label, x, currY);
      doc.setFont(undefined, 'normal');
      doc.text(value, x + labelWidth + 1, currY);
      return currY + 7;
    };

    if (userData?.checklistResponse?.length > 0) {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Declaration Form', 20, y);
      doc.setFont(undefined, 'normal');
      y += 15;

      doc.setFontSize(12);
      const fullName = `${userData?.userId?.firstName || ''} ${
        userData?.userId?.lastName || ''
      }`.trim();
      y = addLabelValue('Full Name: ', fullName || 'N/A', 20, y);
      y += 3;
      y = addLabelValue('Email: ', userData?.userId?.email || 'N/A', 20, y);
      y += 3;
      y = addLabelValue('Phone Number: ', userData?.userId?.phone || 'N/A', 20, y);
      y += 15;

      for (let index = 0; index < userData.checklistResponse.length; index++) {
        const item = userData.checklistResponse[index];
        if (y + 10 > pageHeight) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(12);
        y = addWrappedText(
          `${item.type !== 'ACKNOWLEDGEMENT' ? `Q${index + 1}: ` : ''}${item.question}`,
          20,
          y,
          true
        );
        y += 3;

        if (item.type === 'ACKNOWLEDGEMENT' && item.response) {
          if (y + 25 > pageHeight) {
            doc.addPage();
            y = 20;
          }
          try {
            const imageData = item.response.replace(/^data:application\/octet-stream;base64,/, '');
            doc.addImage(`data:image/png;base64,${imageData}`, 'PNG', 20, y, 40, 20);
            y += 25;
          } catch {
            y = addWrappedText('Answer: [Signature]', 20, y);
            y += 3;
          }
        } else if (item.type === 'MEDIA') {
          const media = mediaMap[index];
          const base64 = media?.base64;

          if (base64) {
            try {
              const imgEl = await new Promise((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = reject;
                i.src = base64; // data URI — no crossOrigin needed
              });

              const maxPdfW = 170;
              const maxPdfH = 120;
              const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
              let pdfW = maxPdfW;
              let pdfH = pdfW / ratio;
              if (pdfH > maxPdfH) {
                pdfH = maxPdfH;
                pdfW = pdfH * ratio;
              }

              if (y + pdfH + 5 > pageHeight) {
                doc.addPage();
                y = 20;
              }

              const fmt = base64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
              doc.addImage(base64, fmt, 20, y, pdfW, pdfH);
              y += pdfH + 5;
            } catch {
              y = addWrappedText('Answer: [Media image — could not embed]', 20, y);
              y += 3;
            }
          } else {
            y = addWrappedText(
              media?.loading
                ? 'Answer: [Media still loading — try again]'
                : 'Answer: [Media unavailable]',
              20,
              y
            );
            y += 3;
          }
        } else {
          y = addWrappedText(`Answer: ${item.response || 'N/A'}`, 20, y);
          y += 3;
        }

        y += 5;
      }

      if (userData.createdAt) {
        if (y + 10 > pageHeight) {
          doc.addPage();
          y = 20;
        }
        y += 10;
        y = addWrappedText(
          `Declaration Date: ${moment(userData.createdAt).format('DD-MM-YYYY HH:mm:ss')}`,
          20,
          y,
          true
        );
      }
    }

    doc.save(`Declaration-${fileName}.pdf`);
  };

  const fullName = `${userData?.userId?.firstName || ''} ${
    userData?.userId?.lastName || ''
  }`.trim();

  const contentStyle = { fontSize: '15px' };
  const userInfoStyle = { marginBottom: '10px' };
  const labelStyle = { fontWeight: 'bold', fontSize: '14px' };
  const valueStyle = { fontWeight: 'normal' };
  const infoRowStyle = { fontSize: '14px', lineHeight: '25px', marginBottom: '4px' };
  const questionStyle = { fontSize: '14px', lineHeight: '24px', color: '#fff' };
  const answerStyle = {
    fontSize: '14px',
    lineHeight: '24px',
    color: '#fff',
    whiteSpace: 'pre-wrap',
  };
  const emptyStateStyle = {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#fff',
    fontSize: '16px',
  };
  const dateStyle = { fontSize: '14px' };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Declaration Form"
      icon={
        userData?.checklistResponse?.length > 0 ? (
          <PictureAsPdfIcon onClick={generatePDF} style={{ cursor: 'pointer', color: '#fff' }} />
        ) : null
      }
    >
      <ModalWrapper style={{ minWidth: '600px' }}>
        <Label>
          <div style={contentStyle}>
            <div style={userInfoStyle}>
              <p style={infoRowStyle}>
                <span style={labelStyle}>Full Name: </span>
                <span style={valueStyle}>{fullName || 'N/A'}</span>
              </p>
              <p style={infoRowStyle}>
                <span style={labelStyle}>Email: </span>
                <span style={valueStyle}>{userData?.userId?.email || 'N/A'}</span>
              </p>
              <p style={infoRowStyle}>
                <span style={labelStyle}>Phone Number: </span>
                <span style={valueStyle}>{userData?.userId?.phone || 'N/A'}</span>
              </p>
            </div>

            {userData?.checklistResponse?.length > 0 ? (
              <div>
                {userData.checklistResponse.map((item, index) => (
                  <div key={item.id || index} style={{ marginBottom: '12px' }}>
                    <p style={questionStyle}>
                      {item.type !== 'ACKNOWLEDGEMENT' && `Q${index + 1}: `}
                      {item.question}
                    </p>
                    <div style={answerStyle}>
                      {item.type === 'ACKNOWLEDGEMENT' && item.response ? (
                        <div style={{ marginTop: '10px' }}>
                          <Signature
                            src={`data:image/png;base64,${item.response.replace(
                              /^data:application\/octet-stream;base64,/,
                              ''
                            )}`}
                            alt="Signature"
                          />
                        </div>
                      ) : item.type === 'MEDIA' ? (
                        <div style={{ marginTop: '8px' }}>
                          <strong>Answer:</strong>
                          {(() => {
                            const media = mediaMap[index];
                            // Some checklist questions ask the user to upload a PDF
                            // rather than an image (e.g. "Upload a PDF here (Optional)").
                            // For those, show a "View PDF" link that opens the document
                            // in a new window instead of trying to render it as an <img>.
                            const isPdfQuestion = item.question?.toLowerCase().includes('pdf');

                            if (!item.response) {
                              return <span style={{ marginLeft: '6px', color: '#aaa' }}>N/A</span>;
                            }
                            if (!media || media.loading) {
                              return (
                                <span
                                  style={{
                                    fontSize: '13px',
                                    color: '#aaa',
                                    fontStyle: 'italic',
                                    marginLeft: '6px',
                                  }}
                                >
                                  Loading media...
                                </span>
                              );
                            }
                            if (media.error || !media.src) {
                              return (
                                <span
                                  style={{ fontSize: '13px', color: '#f87171', marginLeft: '6px' }}
                                >
                                  Failed to load media
                                </span>
                              );
                            }

                            if (isPdfQuestion) {
                              return (
                                <a
                                  href={media.src}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    marginLeft: '6px',
                                    color: '#E6B471',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                  }}
                                >
                                  View PDF
                                </a>
                              );
                            }

                            return (
                              <img
                                src={media.src}
                                alt="Media attachment"
                                style={{
                                  display: 'block',
                                  maxWidth: '100%',
                                  maxHeight: '400px',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  borderRadius: '6px',
                                  marginTop: '8px',
                                }}
                              />
                            );
                          })()}
                        </div>
                      ) : (
                        <div>
                          <strong>Answer:</strong> {item.response || 'N/A'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {userData?.createdAt && (
                  <div style={dateStyle}>
                    <p>
                      Declaration Date: {moment(userData.createdAt).format('DD-MM-YYYY HH:mm:ss')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={emptyStateStyle}>
                <p>No checklist responses available</p>
              </div>
            )}
          </div>
        </Label>
      </ModalWrapper>
    </Modal>
  );
};

export default DeclarationModal;
